import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as irregularitiesActions from 'features/irregularities/duck';
import * as taskActions from 'features/task-management/duck';
import * as roomActions from 'features/room-builder/duck';
import * as locationActions from 'shared/ducks/locationDuck';
import * as userActions from 'core/duck';
import * as materialChainActions from 'features/material-chain/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as staffActions from 'features/staff-and-rooms/duck';

import { withTranslation } from 'react-i18next';
import { TcEventIrregularities } from 'features/tc-event-dashboard/index';
import {
  AlertMessage,
  Spinner,
  Stepper,
  AlertNotice,
  EventDashboardDetails,
} from 'shared/components';
import { PermissionsService } from 'shared/services';
import {
  RoomTasks,
  ExitTaskPrompt,
  RoomTestMaterials,
  RoomPolling,
  RoomLtiLaunch,
  RoomManualTiming,
  RoomSeatingChart,
  ReviewAndCloseRoomModal,
  RoomRosterDashboard,
} from 'features/task-management';
import {
  getRoomAvailableTasks,
  getCompletedTasks,
  getUpcomingTasks,
  getCurrentTask,
  getTimersTasks,
  getRoomDTO,
  mapDisplayTexts,
} from 'features/task-management/selectors';
import { getGroupedMaterials } from 'features/material-chain/selectors';
import { mapRoomsByLocation } from 'features/tc-event-dashboard/selectors';
import isEmpty from 'lodash/isEmpty';
import partial from 'lodash/partial';
import { ROOM_STATUS } from 'shared/constants';
import { isRoomOnline, isEventDate, hasRoomMaterialsRequired } from 'shared/helpers';
import EyeIcon from 'assets/svgs/eyeIcon.svg';
import './RoomDashboard.scss';

class RoomDashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMessage: false,
      taskCardOpen: false,
      warningTestDayOpen: false,
      reviewAndCloseModalOpen: false,
      toastMessage: '',
      alreadyCompletedMessage: '',
      showTips: sessionStorage.getItem('showRoomDashboardTips')
        ? JSON.parse(sessionStorage.getItem('showRoomDashboardTips'))
        : false,
    };

    this.progressBarSteps = [
      {
        id: 1,
        title: this.props.t('testCenter.roomDashboard.progressBar.setupCheckInOpenRoom'),
      },
      {
        id: 2,
        title: this.props.t('testCenter.roomDashboard.progressBar.administerTestLabel'),
      },
      {
        id: 3,
        title: this.props.t('testCenter.roomDashboard.progressBar.closeRoomLabel'),
      },
    ];

    this.onAddIrregularity = this.onAddIrregularity.bind(this);
    this.onSubmitTask = this.onSubmitTask.bind(this);
    this.removeCompleted = this.removeCompleted.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.renderTask = this.renderTask.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.closeIrregularityWarningModal = this.closeIrregularityWarningModal.bind(this);
  }

  componentDidMount() {
    const { roomId, eventId } = this.props.match.params;
    const { actions, room, tcEvent, displayTexts, roomTimers } = this.props;

    actions.getRoomStatus(roomId);
    actions.getTCEventMaterialTypes(eventId);
    actions.getTCInfo(eventId);
    this.props.actions.getServerDateTime();

    actions.getRoomMaterials(roomId);
    if (!room.id || room.id !== +roomId || !roomTimers.length) {
      actions.clearCurrentRoom();
      actions.clearRoomStatusAction();
      actions.getRoomCompletedTasks(roomId);
      actions.getRoomTimers(roomId);
      actions.getRoom(roomId).then(() => {
        if (this.props.room.roomType) {
          actions.getRoomTypeMaterialTypes(this.props.room.roomType.id);
        }
      });
    } else if (this.props.room.roomType) {
      actions.getRoomTypeMaterialTypes(room.roomType.id);
    }

    if (isEmpty(tcEvent) || tcEvent.id !== this.props.match.params.eventId) {
      actions.clearTCEventAction();
      actions.getRoomTypesAction({ data: [] });
      actions.getTcEvent(this.props.match.params.eventId).then(() => {
        if (!isEmpty(this.props.tcEvent)) {
          const cycleId = this.props.tcEvent.testingEvent.adminId.cycleId.id;
          actions.getTasks(cycleId);
          actions.getRoomTypes(this.props.match.params.eventId);
          actions.getIrregularityTypes({ cycleId: cycleId });

          this.props.actions.getTestCenterCycle(this.props.match.params.eventId);

          if (
            isEmpty(displayTexts) ||
            !displayTexts.room_tips_for_success.cycle ||
            displayTexts.room_tips_for_success.cycle.id !== cycleId
          ) {
            actions.getDisplayTexts(cycleId);
          }
          this.setState({
            isTestDay: isEventDate(this.props.tcEvent.testingEvent, this.props.serverDateTime),
          });

          if (
            PermissionsService.hasTestCenterManage(this.props.tcEvent.testCenterCycleId) ||
            PermissionsService.hasEventManage(eventId) ||
            PermissionsService.hasRoomAccess(eventId)
          ) {
            actions.getRoomsDTO(eventId);
          } else {
            actions.getRoomDTO(roomId);
          }

          actions.getTcEventStaff(eventId);
        }
      });
    } else {
      const cycleId = tcEvent.testingEvent.adminId.cycleId.id;
      actions.getTasks(cycleId);
      actions.getRoomTypes(this.props.match.params.eventId);

      if (isEmpty(displayTexts) || displayTexts.room_tips_for_success.cycle.id !== cycleId) {
        actions.getDisplayTexts(cycleId);
      }
      this.setState({
        isTestDay: isEventDate(tcEvent.testingEvent, this.props.serverDateTime),
      });

      if (
        PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
        PermissionsService.hasEventManage(eventId) ||
        PermissionsService.hasRoomAccess(eventId)
      ) {
        actions.getRoomsDTO(eventId);
      } else {
        actions.getRoomDTO(roomId);
      }
    }

    const prevRoom = JSON.parse(sessionStorage.getItem('room'));
    if (prevRoom && prevRoom.added) {
      this.setState({
        showMessage: true,
        toastMessage: 'irregularities.alertIrregularitySubmittedMessage',
        showMessageTime: 3000,
      });
      sessionStorage.removeItem('room');
    }

    if (!this.poller) {
      this.poller = new RoomPolling({
        action: partial(actions.getRoomStatus, roomId),
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.manualTimerSet && this.props.roomDTO.manualTimerFlag) {
      this.setState({
        toastMessage: 'testCenter.manualTimer.successMessage',
        showMessage: true,
      });
      this.props.actions.setRoomManualTimerAction({});
    }

    const { roomStatus, roomTypes } = this.props;
    const { actions } = this.props;
    const { roomId } = this.props.match.params;

    if (
      this.props.timerWarning !== prevProps.timerWarning &&
      this.props.timerWarning &&
      this.state.taskCardOpen
    ) {
      this.setState({
        taskCardOpen: false,
      });
    }

    if (
      !isEmpty(roomStatus) &&
      !roomStatus.loading &&
      (isEmpty(prevProps.roomStatus) || prevProps.roomStatus.loading)
    ) {
      if (
        roomTypes.length &&
        ((roomStatus.roomType && roomStatus.roomType !== prevProps.roomStatus?.roomType) ||
          (!this.props.roomTypeMaterialTypes.length &&
            !this.props.loadingRoomMaterialTypes &&
            roomStatus.roomType))
      ) {
        const currentType = roomTypes.find((type) => type.name === roomStatus.roomType);

        if (!isEmpty(currentType)) {
          actions.getRoomTypeMaterialTypes(currentType.id);
        }
      }

      if (
        !isEmpty(this.props.room) &&
        !this.props.room.loading &&
        this.props.room.status !== roomStatus.status
      ) {
        actions.getRoom(roomId);
        actions.getRoomStatus(roomId);
        actions.getRoomMaterials(roomId);
        if (roomStatus.status === ROOM_STATUS.opened) {
          actions.getRoomTimers(roomId);
          actions.getRoomCompletedTasks(roomId);
        }
      }
    }

    if (
      !isEmpty(this.props.roomDTO) &&
      this.props.roomDTO.manualTimerFlag &&
      (isEmpty(prevProps.roomDTO) || !prevProps.roomDTO.manualTimerFlag) &&
      this.poller
    ) {
      this.poller.stop();
      this.poller = null;
    }

    if (
      this.props.location.state?.successNotif &&
      prevProps.location.state?.successNotif !== this.props.location.state.successNotif
    ) {
      this.setState({
        showMessage: true,
        toastMessage: this.props.location.state.successNotif,
      });
      globalThis.history.replaceState(null, '');
    }
  }

  componentWillUnmount() {
    const { tcEvent, actions, user, room } = this.props;
    if (this.poller) {
      this.poller.stop();
      this.poller = null;
    }

    if (user.testCenterEventPersonnels.some((ev) => room && ev.roomId === room.id)) {
      return;
    }

    if (
      (PermissionsService.hasEventManage(tcEvent.id) ||
        PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId)) &&
      globalThis.location.pathname.indexOf('manual-timer') === -1
    ) {
      actions.clearCurrentRoom();
    }
  }

  onAddIrregularity() {
    const { room, history } = this.props;

    const { params } = this.props.match;

    if (!this.state.isTestDay && !this.state.warningTestDayOpen) {
      this.setState({
        warningTestDayOpen: true,
      });
      return;
    }
    sessionStorage.setItem(
      'room',
      JSON.stringify({
        id: room.id,
        roomName: room.name,
      })
    );
    history.push(`/irregularities/type/${params.eventId}/${params.roomId}`);
  }

  async onSubmitTask(currentTask) {
    const { roomId } = this.props.match.params;
    const { actions } = this.props;

    const completedTask = await actions.setCompleteTask(roomId, currentTask);

    if (!completedTask.id) {
      this.setState({
        alreadyCompletedMessage: completedTask.data.message,
      });
      if (completedTask.status === 400) {
        this.props.actions.getRoomStatus(this.props.match.params.roomId);
      }
    }

    this.toggleModal();
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  removeCompleted() {
    this.setState({
      alreadyCompletedMessage: '',
    });
  }

  toggleModal() {
    this.setState((prevState) => ({
      taskCardOpen: !prevState.taskCardOpen,
    }));
  }

  closeIrregularityWarningModal() {
    this.setState({
      warningTestDayOpen: false,
    });
  }

  renderTask(task, active) {
    this.setState({
      currentTask: {
        ...task,
        active,
      },
    });

    if (active && !this.state.taskCardOpen) {
      sessionStorage.setItem(
        'taskInProgress',
        JSON.stringify({
          name: task.name,
        })
      );
    }

    this.toggleModal();
  }

  closeReviewAndCloseModal = (showToast) => {
    this.setState({
      reviewAndCloseModalOpen: false,
    });

    if (showToast) {
      this.setState({
        showMessage: true,
        toastMessage: 'reviewAndCloseRoom.successMessage',
      });
    }
  };

  goToCloseRoomPage = () => {
    const { tcEvent, room } = this.props;

    if (!tcEvent.specialTestingFlag) {
      this.setState({
        reviewAndCloseModalOpen: true,
      });
    } else {
      this.props.history.push(`/test-event/${tcEvent.id}/room/${room.id}/close-room`);
    }
  };

  render() {
    const {
      tcEvent,
      irregularities,
      match,
      room,
      completedTasks,
      upcomingTasks,
      roomAvailableTasks,
      timersTasks,
      roomStatus,
      roomDTO,
      history,
      roomGroupedMaterials,
      roomTypeMaterialTypes,
      loadingTasks,
      locations,
      loadingCompleteTask,
      irregularityTypes,
      user,
      testCenterCycle,
    } = this.props;

    const { currentTask, reviewAndCloseModalOpen, alreadyCompletedMessage } = this.state;

    const {
      testCenterName,
      locationName,
      locationArea,
      reviewedIrregularityCount,
      submittedIrregularityCount,
      roomType,
      roomTiming,
    } = roomStatus;

    if (
      roomStatus.loading ||
      !testCenterName ||
      isEmpty(roomDTO) ||
      loadingTasks ||
      isEmpty(tcEvent)
    ) {
      return <Spinner />;
    }

    let progressBarStep;

    switch (roomStatus.status) {
      case ROOM_STATUS.opened:
        if (
          (!roomDTO.manualTimerFlag && completedTasks.length !== roomAvailableTasks.length) ||
          (roomDTO.manualTimerFlag && timersTasks.some((timer) => !timer.startDateTime))
        ) {
          progressBarStep = 1;
        } else {
          progressBarStep = 2;
        }
        break;
      case ROOM_STATUS.closed:
        progressBarStep = 3;
        break;
      default:
        progressBarStep = 0;
    }

    return (
      <div
        className={`tc-event-container room-dashboard-container col ${
          roomDTO.manualTimerFlag ? 'extend' : ''
        }`}>
        <div className="">
          <Stepper current={progressBarStep} stepsTotal={this.progressBarSteps} />
          <div className="position-relative header-section">
            <h4 className="page-title mb-0">{this.props.t('navigation.roomDashboard')}</h4>
            <EventDashboardDetails
              date={!isEmpty(tcEvent) ? tcEvent.testingEvent.name : ''}
              name={testCenterName}>
              <span>{locationName}</span>
              {locationArea && locationArea.length > 0 && <span>, {locationArea}</span>}
            </EventDashboardDetails>
            <h5 className="mt-24">{room.name}</h5>
            <div className="room-type-timing-section">
              <div className="d-inline-block">
                {roomType || this.props.t('testCenter.roomDashboard.noTypeLabel')}
              </div>
              {roomTiming && (
                <div className="d-inline-block ml-2">
                  {this.props.mappedRoomTiming[roomTiming]?.name}
                </div>
              )}
              {this.props.room.roomType && (
                <div className="d-inline-block ml-2">
                  {this.props.room.roomType.onlineFlag
                    ? this.props.t('common.labels.online')
                    : this.props.t('common.labels.paper')}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="room-dashboard-content">
          <div className="border-box-silver">
            <RoomLtiLaunch />
            {!roomDTO.manualTimerFlag ? (
              <RoomTasks
                currentTask={currentTask || this.props.currentTask}
                alreadyCompletedMessage={alreadyCompletedMessage}
                completedTasks={completedTasks}
                upcomingTasks={upcomingTasks}
                eventId={match.params.eventId}
                roomId={match.params.roomId}
                taskCardOpen={this.state.taskCardOpen}
                toggleContentModal={this.toggleModal}
                roomAvailableTasks={roomAvailableTasks}
                roomStatus={roomStatus}
                userId={this.props.user.id}
                loadingCompleteTask={loadingCompleteTask}
                onSubmitTask={this.onSubmitTask}
                removeCompleted={this.removeCompleted}
                renderTask={this.renderTask}
                isTestDay={this.state.isTestDay}
                roomGroupedMaterials={roomGroupedMaterials}
                roomTypeMaterialTypes={roomTypeMaterialTypes}
                room={room}
                tcEvent={tcEvent}
                locations={locations}
                history={history}
                goToCloseRoomPage={this.goToCloseRoomPage}
                roomType={roomType}
              />
            ) : (
              <RoomManualTiming
                timersTasks={timersTasks}
                eventId={match.params.eventId}
                roomId={match.params.roomId}
                roomStatus={roomStatus}
                history={history}
                room={room}
                goToCloseRoomPage={this.goToCloseRoomPage}
              />
            )}
          </div>
          {!roomDTO.manualTimerFlag &&
            roomStatus.status !== ROOM_STATUS.closed &&
            upcomingTasks.length > 0 && (
              <div className="padding-t-24 preview-tasks-box">
                <h5>{this.props.t('taskManagement.previewTasksSubTitle')}</h5>
                <div className="border-box-silver">
                  <p>{this.props.t('taskManagement.previewTasksMessage')}</p>
                  <button
                    onClick={() =>
                      history.push(`/test-event/${match.params.eventId}/room/${room.id}/tasks`)
                    }
                    className="btn btn-primary btn-small">
                    <EyeIcon className="eye-icon" />

                    {this.props.t('taskManagement.previewTasksLabel')}
                  </button>
                </div>
              </div>
            )}

          <RoomRosterDashboard eventId={match.params.eventId} roomId={match.params.roomId} />

          {!tcEvent.specialTestingFlag && testCenterCycle?.cycle?.seatingChartRequiredFlag && (
            <RoomSeatingChart
              eventId={match.params.eventId}
              roomId={match.params.roomId}
              history={history}
              room={room}
              roomStatus={roomStatus}
              tcEvent={tcEvent}
            />
          )}

          <div className="mt-24 mb-24">
            <h5>
              {this.props.t('testCenter.eventDashboard.irregularitiesTitle', {
                number: (reviewedIrregularityCount || 0) + (submittedIrregularityCount || 0),
              })}
            </h5>
            <div className="border-box-silver">
              <TcEventIrregularities
                roomStatus={roomStatus}
                irregularities={irregularities}
                tcEvent={tcEvent}
                roomId={room.id}
                onAdd={this.onAddIrregularity}
                onCloseWarningModal={this.closeIrregularityWarningModal}
                warningTestDayOpen={this.state.warningTestDayOpen}
              />
            </div>
          </div>
          {!isRoomOnline(room.roomType) && hasRoomMaterialsRequired(room.roomType) ? (
            <RoomTestMaterials
              roomGroupedMaterials={roomGroupedMaterials}
              roomStatus={roomStatus}
              roomId={match.params.roomId}
              roomTypeMaterialTypes={roomTypeMaterialTypes}
              room={room}
            />
          ) : (
            <div className="mt-24 mb-24">
              <h5>{this.props.t('roomDashboard.testDetails.title')}</h5>
              <div className="border-box-silver">
                <AlertNotice
                  message={
                    roomStatus.status === ROOM_STATUS.closed
                      ? this.props.t(
                          tcEvent.specialTestingFlag
                            ? 'roomDashboard.testDetails.genericSuccessMessage'
                            : 'roomDashboard.testDetails.successMessage'
                        )
                      : this.props.t(
                          tcEvent.specialTestingFlag
                            ? 'roomDashboard.testDetails.genericInfoMessage'
                            : 'roomDashboard.testDetails.infoMessage'
                        )
                  }
                  type={roomStatus.status === ROOM_STATUS.closed ? 'success' : ''}
                />
              </div>
            </div>
          )}
        </div>
        <button className="btn btn-outline m-0 btn-back" onClick={this.props.history.goBack}>
          {this.props.t('common.labels.back')}
        </button>

        <ReviewAndCloseRoomModal
          roomId={room.id}
          completedTasks={completedTasks}
          roomAvailableTasks={roomAvailableTasks}
          roomStatus={roomStatus}
          user={user}
          irregularityTypes={irregularityTypes}
          actions={this.props.actions}
          room={room}
          manualTimerFlag={roomDTO.manualTimerFlag}
          reviewAndCloseModalOpen={reviewAndCloseModalOpen}
          closeReviewAndCloseModal={this.closeReviewAndCloseModal}
          testCenterCycle={testCenterCycle}
        />
        <AlertMessage
          show={this.state.showMessage}
          time={this.state.showMessageTime}
          removeMessage={this.removeMessage}
          message={this.state.toastMessage}
        />
        <ExitTaskPrompt />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.users.currentUser || {},
    roomStatus: state.taskManagement.roomStatus,
    roomTimers: state.taskManagement.roomTimers,
    room: state.roomOptions.room || {},
    tcEvent: state.tcEvent.currentEvent || {},
    irregularities: state.irregularities.roomIrregularities || [],
    rooms: state.tcEvent.rooms || [],
    testCenterCycle: state.tcEvent.testCenterCycle || {},
    roomTypes: state.statics.roomTypes,
    serverDateTime: state.statics.serverDateTime,
    displayTexts: mapDisplayTexts(state),
    timerWarning: state.taskManagement.timerWarning,
    tcEventMaterialTypes: state.materialChain.tcEventMaterialTypes,
    roomTypeMaterialTypes: state.materialChain.roomTypeMaterialTypes,
    loadingRoomMaterialTypes: state.materialChain.loadingRoomMaterialTypes,
    completedTasks: getCompletedTasks(state),
    upcomingTasks: getUpcomingTasks(state),
    currentTask: getCurrentTask(state),
    roomAvailableTasks: getRoomAvailableTasks(state),
    timersTasks: getTimersTasks(state),
    roomDTO: getRoomDTO(state),
    roomGroupedMaterials: getGroupedMaterials(state),
    loadingTasks: state.taskManagement.loadingTasks,
    locations: mapRoomsByLocation(state),
    loadingCompleteTask: state.taskManagement.loadingCompleteTask,
    irregularityTypes: state.statics.irregularityTypes || [],
    manualTimerSet: state.tcEvent.manualTimerSet,
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...tcEventActions,
        ...irregularitiesActions,
        ...taskActions,
        ...roomActions,
        ...locationActions,
        ...userActions,
        ...materialChainActions,
        ...staticActions,
        ...staffActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(RoomDashboard))
);

RoomDashboard.propTypes = {
  t: PropTypes.func.isRequired,
  tcEvent: PropTypes.shape({}).isRequired,
  irregularities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  timerWarning: PropTypes.bool,
  testCenterCycle: PropTypes.shape({
    name: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  roomTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  room: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    roomType: PropTypes.shape({
      id: PropTypes.number,
    }),
    timing: PropTypes.string,
  }).isRequired,
  completedTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  upcomingTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roomAvailableTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roomStatus: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  roomDTO: PropTypes.shape({
    manualTimerFlag: PropTypes.bool,
  }),
  actions: PropTypes.shape({
    getTasks: PropTypes.func.isRequired,
    getTcEvent: PropTypes.func.isRequired,
    getTestCenterCycle: PropTypes.func.isRequired,
    getTcEventIrregularities: PropTypes.func.isRequired,
    getRoomTypes: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    updateRoom: PropTypes.func.isRequired,
    getRoomTimers: PropTypes.func.isRequired,
    getRoomsDTO: PropTypes.func.isRequired,
    resetHideWarningAction: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string,
      roomId: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

RoomDashboard.defaultProps = {
  timerWarning: false,
};
